import React, { useCallback, useState } from "react";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import useCalendarEventQuery from "../hooks/useCalendarEventQuery";
import useUpsertCalendarEventMutation from "../hooks/useUpsertCalendarEventMutation";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSBox from "../../system-design/DSBox";
import DSGrid from "../../system-design/DSGrid";
import DSTextField from "../../system-design/DSTextField";
import DSInputLabel from "../../system-design/DSInputLabel";
import DSFormControl from "../../system-design/DSFormControl";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";
import {
  CalendarEvent,
  CalendarEventsType,
  GeographicRegion,
  State,
} from "../../../api/calendarEvent";
import StringChipEditor from "../../../components/Widgets/StringChipEditor";
import CountryDropdown from "./CountryDropdown";
import StateDropdown from "./StateDropdown";
import DSChip from "../../system-design/DSChip";
import DeleteIcon from "@mui/icons-material/Delete";
import { areStatesEqual } from "../../../utils/util";

type CalendarEventEditViewProps = {
  calendarEventId: string;
  onClose: () => void;
};

const CalendarEventEditView = ({
  calendarEventId,
  onClose,
}: CalendarEventEditViewProps) => {
  const { mutateAsync: upsertCalendarEvent } = useUpsertCalendarEventMutation();
  const { data, isLoading } = useCalendarEventQuery(calendarEventId);

  const emptyCalendarEvent: CalendarEvent = {
    id: calendarEventId,
    name: "",
    description: "",
    date: {
      year: 0,
      month: 0,
      day: 0,
    },
    country: undefined,
    states: [],
    primaryType: "",
    type: [],
  };

  const [calendarEvent, setCalendarEvent] = useState<CalendarEvent>(
    data || emptyCalendarEvent,
  );

  const isFormValid = () => {
    return (
      (calendarEvent.name ?? "").trim() !== "" &&
      (calendarEvent.primaryType ?? "").trim() !== "" &&
      calendarEvent.date.year > 0 &&
      calendarEvent.date.month > 0 &&
      calendarEvent.date.day > 0
    );
  };

  const normalizeEventType = (type: string) => {
    const validTypes = Object.values(CalendarEventsType);
    return validTypes.includes(type as CalendarEventsType)
      ? type
      : CalendarEventsType.OTHER;
  };

  const handleCountryChange = (country: GeographicRegion | null) => {
    setCalendarEvent((prev) => ({
      ...prev,
      country: country || undefined,
      states: [],
    }));
  };

  const handleSave = useCallback(() => {
    upsertCalendarEvent({
      eventId: calendarEventId,
      event: {
        id: calendarEventId,
        name: calendarEvent.name,
        description: calendarEvent.description,
        date: calendarEvent.date,
        country: calendarEvent.country || undefined,
        primaryType: calendarEvent.primaryType,
        type: calendarEvent.type,
        states: calendarEvent.states,
      },
    });
    onClose();
  }, [upsertCalendarEvent, calendarEventId, calendarEvent, onClose]);

  const handleStateSelect = (state: State | null) => {
    if (state && !calendarEvent.states?.some((s) => areStatesEqual(s, state))) {
      setCalendarEvent((prev) => ({
        ...prev,
        states: [...(prev.states || []), state],
      }));
    }
  };

  const handleRemoveState = (stateToRemove: State) => {
    setCalendarEvent((prev) => ({
      ...prev,
      states: (prev.states || []).filter(
        (state) => !areStatesEqual(state, stateToRemove),
      ),
    }));
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <>
      <DSDialogContent>
        <DSBox sx={{ p: 2 }}>
          <DSGrid container spacing={3}>
            <DSGrid item xs={12}>
              <DSTextField
                fullWidth
                label="Event Name"
                value={calendarEvent.name}
                onChange={(e) =>
                  setCalendarEvent((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                required
              />
            </DSGrid>
            <DSGrid item xs={12}>
              <DSTextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={calendarEvent.description}
                onChange={(e) =>
                  setCalendarEvent((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                required
              />
            </DSGrid>
            <DSGrid item xs={4}>
              <DSTextField
                fullWidth
                label="Year"
                type="number"
                value={calendarEvent.date.year}
                onChange={(e) =>
                  setCalendarEvent((prev) => ({
                    ...prev,
                    date: {
                      ...prev.date,
                      year:
                        parseInt(e.target.value) || new Date().getFullYear(),
                    },
                  }))
                }
                required
              />
            </DSGrid>
            <DSGrid item xs={4}>
              <DSTextField
                fullWidth
                label="Month"
                type="number"
                inputProps={{ min: 1, max: 12 }}
                value={calendarEvent.date.month}
                onChange={(e) =>
                  setCalendarEvent((prev) => ({
                    ...prev,
                    date: {
                      ...prev.date,
                      month: parseInt(e.target.value) || 1,
                    },
                  }))
                }
                required
              />
            </DSGrid>
            <DSGrid item xs={4}>
              <DSTextField
                fullWidth
                label="Day"
                type="number"
                inputProps={{ min: 1, max: 31 }}
                value={calendarEvent.date.day}
                onChange={(e) =>
                  setCalendarEvent((prev) => ({
                    ...prev,
                    date: {
                      ...prev.date,
                      day: parseInt(e.target.value) || 1,
                    },
                  }))
                }
                required
              />
            </DSGrid>
            <DSGrid item xs={12}>
              <DSBox>
                <DSGrid container spacing={2} sx={{ mb: 2 }}>
                  <DSGrid item xs={12}>
                    <CountryDropdown
                      initialCountry={calendarEvent.country || null}
                      onSelect={handleCountryChange}
                    />
                  </DSGrid>
                  <DSGrid item xs={10}>
                    <StateDropdown
                      country={calendarEvent.country || null}
                      onSelect={handleStateSelect}
                      disabled={!calendarEvent.country}
                    />
                  </DSGrid>
                </DSGrid>
                <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {calendarEvent.states?.map((state, index) => (
                    <DSChip
                      key={index}
                      label={`${state.name} (${state.iso})`}
                      onDelete={() => handleRemoveState(state)}
                      deleteIcon={<DeleteIcon />}
                      variant="outlined"
                    />
                  ))}
                </DSBox>
              </DSBox>
            </DSGrid>
            <DSGrid item xs={12}>
              <DSFormControl fullWidth required>
                <DSInputLabel>Primary Type</DSInputLabel>
                <DSSelect
                  value={normalizeEventType(calendarEvent.primaryType)}
                  label="Primary Type"
                  onChange={(e) =>
                    setCalendarEvent((prev) => ({
                      ...prev,
                      primaryType: e.target.value as string,
                    }))
                  }
                  required
                >
                  <DSMenuItem value="">
                    <em>None</em>
                  </DSMenuItem>
                  {Object.values(CalendarEventsType).map((eventType) => (
                    <DSMenuItem key={eventType} value={eventType}>
                      {eventType}
                    </DSMenuItem>
                  ))}
                </DSSelect>
              </DSFormControl>
            </DSGrid>
            <DSGrid item xs={12}>
              <StringChipEditor
                label="Add Type"
                values={calendarEvent.type}
                onChange={(newTypes) =>
                  setCalendarEvent((prev) => ({ ...prev, type: newTypes }))
                }
                placeholder="Enter event type"
                helperText="Add event types one at a time"
              />
            </DSGrid>
          </DSGrid>
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={() => onClose()}>Close</DSSecondaryButton>
        <DSPrimaryButton disabled={!isFormValid()} onClick={handleSave}>
          Save
        </DSPrimaryButton>
      </DSDialogActions>
    </>
  );
};

export default CalendarEventEditView;
